import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { PortalAttendanceKpiResponse } from "@/actions/ssr/attendance/portal/getPortalAttendanceKpiSSR";
import type { PortalAttendanceShiftDetailsResponse } from "@/actions/ssr/attendance/portal/getPortalAttendanceShiftDetailsSSR";
import type { PortalAttendanceOverviewResponse } from "@/actions/ssr/attendance/portal/getPortalAttendanceOverviewSSR";
import type { PortalAttendanceCalendarResponse } from "@/actions/ssr/attendance/portal/getPortalAttendanceCalendarSSR";
import type { PortalAttendanceLogResponse } from "@/actions/ssr/attendance/portal/getPortalAttendanceLogSSR";

interface PortalAttendanceState {
  kpi: PortalAttendanceKpiResponse;
  shiftDetails: PortalAttendanceShiftDetailsResponse;
  overview: PortalAttendanceOverviewResponse;
  calendar: PortalAttendanceCalendarResponse;
  log: PortalAttendanceLogResponse;
  overviewClientFetched: boolean;
  calendarClientFetched: boolean;
  logClientFetched: boolean;
}

const initialState: PortalAttendanceState = {
  kpi: {
    present_days: 0,
    present_days_trend: null,
    absent_days: null,
    late_count: null,
    late_count_trend: null,
    total_hours: {
      value_minutes: 0,
      value_minutes_paid_aware: 0,
      display: "0h 0m",
      display_paid_aware: "0h 0m",
      trend: null,
      target_hours: null,
      target_completion_pct: null,
    },
  },
  shiftDetails: {
    date: "",
    office_hours: null,
    office_hours_actual: null,
    break_is_paid: false,
    working_hours_paid_aware: null,
    clock_in: null,
    clock_out: null,
    break_budget_minutes: null,
    total_break_minutes: 0,
    breaks: [],
  },
  overview: {
    present: 0,
    leave: 0,
    late: null,
    absent: null,
  },
  calendar: {
    year: 0,
    month: 0,
    days: [],
  },
  log: {
    count: 0,
    next: null,
    previous: null,
    total_pages: 0,
    current_page: 1,
    results: [],
  },
  overviewClientFetched: false,
  calendarClientFetched: false,
  logClientFetched: false,
};

const portalAttendanceSlice = createSlice({
  name: "portalAttendance",
  initialState,
  reducers: {
    setPortalKpi(state, action: PayloadAction<PortalAttendanceKpiResponse>) {
      state.kpi = action.payload;
    },
    setPortalShiftDetails(
      state,
      action: PayloadAction<PortalAttendanceShiftDetailsResponse>,
    ) {
      state.shiftDetails = action.payload;
    },
    setPortalOverview(
      state,
      action: PayloadAction<PortalAttendanceOverviewResponse>,
    ) {
      state.overviewClientFetched = true;
      state.overview = action.payload;
    },
    setPortalOverviewFromSSR(
      state,
      action: PayloadAction<PortalAttendanceOverviewResponse>,
    ) {
      if (state.overviewClientFetched) return;
      state.overview = action.payload;
    },
    setPortalCalendar(
      state,
      action: PayloadAction<PortalAttendanceCalendarResponse>,
    ) {
      state.calendarClientFetched = true;
      state.calendar = action.payload;
    },
    setPortalCalendarFromSSR(
      state,
      action: PayloadAction<PortalAttendanceCalendarResponse>,
    ) {
      if (state.calendarClientFetched) return;
      state.calendar = action.payload;
    },
    setPortalLog(state, action: PayloadAction<PortalAttendanceLogResponse>) {
      state.logClientFetched = true;
      state.log = action.payload;
    },
    setPortalLogFromSSR(
      state,
      action: PayloadAction<PortalAttendanceLogResponse>,
    ) {
      if (state.logClientFetched) return;
      state.log = action.payload;
    },
  },
});

export const {
  setPortalKpi,
  setPortalShiftDetails,
  setPortalOverview,
  setPortalOverviewFromSSR,
  setPortalCalendar,
  setPortalCalendarFromSSR,
  setPortalLog,
  setPortalLogFromSSR,
} = portalAttendanceSlice.actions;
export default portalAttendanceSlice.reducer;
